import React, { useState } from "react";
import PropTypes from "prop-types";
import { NeoSideMenu, NeoSearch, NeoButton, NeoTabs } from "../../../../../components/Common";
import { WORKOUT_TYPES } from "../../constants";
import { getTemplateSummary } from "../../utils/workoutSummary";
import { useTenant } from "../../../../../hooks/useTenant";

const BRANCH_TO_WORKOUT_TYPE = {
    musculacao: "strength",
    crossfit: "wod",
    natacao: "swimming",
    corrida: "running",
};

const TABS = [
    { id: "templates", label: "Modelos", icon: "mdi-clipboard-text-outline" },
    { id: "prescriptions", label: "Prescrições", icon: "mdi-account-clock-outline" },
];

/**
 * Lista de modelos de treino — usa NeoSideMenu com NeoTabs + chips de modalidade no header.
 */
const TemplateList = ({
    templates = [],
    activeId,
    onSelect,
    onCreateNew,
    loading = false,
    activeTab,
    onChangeTab,
    templatesBadge = 0,
    prescriptionsBadge = 0,
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [filterModality, setFilterModality] = useState("all");
    const { primaryBranch = [] } = useTenant();

    const allowedWorkoutTypes = primaryBranch.length > 0
        ? primaryBranch.map(b => BRANCH_TO_WORKOUT_TYPE[b]).filter(Boolean)
        : null;

    const filteredWorkoutTypes = allowedWorkoutTypes
        ? WORKOUT_TYPES.filter(t => allowedWorkoutTypes.includes(t.value))
        : WORKOUT_TYPES;

    const modalityTabs = [{ value: "all", label: "Todos" }, ...filteredWorkoutTypes];

    const visibleTemplates = templates
        .filter(t => {
            if (allowedWorkoutTypes) {
                const summary = getTemplateSummary(t);
                if (!allowedWorkoutTypes.includes(summary.workoutType)) return false;
            }
            if (filterModality === "all") return true;
            const summary = getTemplateSummary(t);
            return summary.workoutType === filterModality;
        })
        .filter(t => !searchQuery || t.title?.toLowerCase().includes(searchQuery.toLowerCase()));

    const tabs = TABS.map(t => ({
        ...t,
        badge: t.id === "templates" ? (templatesBadge > 0 ? templatesBadge : null)
            : (prescriptionsBadge > 0 ? prescriptionsBadge : null),
    }));

    // Mapear templates para os itens padrão do NeoSideMenu
    const sideMenuItems = visibleTemplates.map((template) => {
        const summary = getTemplateSummary(template);
        const typeConfig = summary.typeConfig;
        const duration = template.totalWeeks ? `${template.totalWeeks}sem` : "";
        const frequency = template.weeklyFrequency ? `${template.weeklyFrequency}x` : "";
        const levelStr = template.level ? `· ${template.level}` : "";
        const freqStr = frequency ? `· ${frequency}` : "";
        const durStr = duration ? `· ${duration}` : "";

        return {
            id: template.id,
            label: template.title || "Sem título",
            description: `${typeConfig.label} ${levelStr} ${freqStr} ${durStr}`,
            color: typeConfig.color, // Mostra o círculo dinâmico com a cor da modalidade
            icon: typeConfig.icon
        };
    });

    const searchComponent = (
        <div className="d-flex flex-column gap-2">
            {/* Tabs — navegação Modelos | Prescrições */}
            <NeoTabs
                activeTab={activeTab}
                onChangeTab={onChangeTab}
                tabs={tabs}
                style={{ marginBottom: "0px" }}
            />

            {/* Chips de modalidade — só mostra se há mais de uma opção */}
            {filteredWorkoutTypes.length > 1 && (
                <div className="d-flex gap-1 flex-wrap mb-1">
                    {modalityTabs.map(tab => (
                        <NeoButton
                            key={tab.value}
                            size="sm"
                            variant={filterModality === tab.value ? "soft-primary" : "soft-secondary"}
                            onClick={() => setFilterModality(tab.value)}
                            style={{ height: "26px", fontSize: "11px", fontWeight: "700" }}
                        >
                            {tab.label}
                        </NeoButton>
                    ))}
                </div>
            )}

            <NeoSearch
                size="sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar modelos..."
            />
        </div>
    );

    return (
        <NeoSideMenu
            title="Biblioteca"
            subtitle={`${templates.length} modelo${templates.length !== 1 ? "s" : ""}`}
            icon="mdi-clipboard-text-outline"
            loading={loading}
            items={sideMenuItems}
            activeId={activeId}
            onSelect={(id) => {
                const template = templates.find(t => t.id === id);
                if (template) onSelect(template);
            }}
            onAdd={onCreateNew}
            searchComponent={searchComponent}
        />
    );
};

TemplateList.propTypes = {
    templates: PropTypes.array,
    activeId: PropTypes.string,
    onSelect: PropTypes.func,
    onCreateNew: PropTypes.func,
    loading: PropTypes.bool,
    activeTab: PropTypes.string,
    onChangeTab: PropTypes.func,
    templatesBadge: PropTypes.number,
    prescriptionsBadge: PropTypes.number,
};

export default TemplateList;
